import { AlertTriangle, Info, MinusCircle } from "lucide-react";

import { FAQ_HEADING, headingIds, slugify } from "@/components/docs/headings";
import { renderInline } from "@/components/docs/inline";
import type { DocBlock } from "@/content/docs/types";
import { cn } from "@/lib/cn";

const CALLOUT = {
  info: {
    Icon: Info,
    className: "border-primary/30 bg-primary/[0.07]",
    iconClassName: "text-primary",
    fallbackTitle: "Note",
  },
  warning: {
    Icon: AlertTriangle,
    className: "border-warning/40 bg-warning/10",
    iconClassName: "text-warning",
    fallbackTitle: "Important",
  },
  limits: {
    Icon: MinusCircle,
    className: "border-border bg-muted/50",
    iconClassName: "text-subtle-foreground",
    fallbackTitle: "What this does not do",
  },
} as const;

export function DocBlocks({ blocks }: { blocks: readonly DocBlock[] }) {
  const ids = headingIds(blocks);
  return (
    <div className="tracking-normal">
      {blocks.map((block, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: blocks are a static authored sequence
        <DocBlockView key={index} block={block} id={ids.get(index)} />
      ))}
    </div>
  );
}

function DocBlockView({ block, id }: { block: DocBlock; id?: string }) {
  switch (block.type) {
    case "lead":
      return (
        <p className="mb-8 text-pretty text-lg leading-relaxed text-foreground">
          {renderInline(block.text)}
        </p>
      );

    case "p":
      return (
        <p className="my-4 leading-relaxed text-muted-foreground">{renderInline(block.text)}</p>
      );

    case "h2":
      return (
        <h2
          id={id}
          className="mt-12 mb-3 scroll-mt-24 text-pretty font-sans text-2xl font-semibold tracking-tight text-foreground"
        >
          {block.text}
        </h2>
      );

    case "h3":
      return (
        <h3
          id={id}
          className="mt-8 mb-2 scroll-mt-24 text-pretty font-sans text-lg font-semibold text-foreground"
        >
          {block.text}
        </h3>
      );

    case "ul":
      return (
        <ul className="my-4 list-disc space-y-2 pl-5 text-muted-foreground marker:text-subtle-foreground">
          {block.items.map((item) => (
            <li key={item} className="leading-relaxed">
              {renderInline(item)}
            </li>
          ))}
        </ul>
      );

    case "ol":
      return (
        <ol className="my-4 list-decimal space-y-2 pl-5 text-muted-foreground marker:text-subtle-foreground">
          {block.items.map((item) => (
            <li key={item} className="leading-relaxed">
              {renderInline(item)}
            </li>
          ))}
        </ol>
      );

    case "steps":
      return (
        <ol className="my-6 space-y-6 border-l border-border pl-9">
          {block.items.map((step, i) => (
            <li key={step.title} className="relative">
              <span
                aria-hidden
                className="absolute -left-[2.98rem] flex h-6 w-6 items-center justify-center rounded-full border border-border bg-card font-mono text-xs text-primary"
              >
                {i + 1}
              </span>
              <p className="font-medium text-foreground">{step.title}</p>
              <p className="mt-1 leading-relaxed text-muted-foreground">
                {renderInline(step.text)}
              </p>
              {step.code ? <CodeBlock code={step.code} className="mt-3" /> : null}
            </li>
          ))}
        </ol>
      );

    case "table":
      return (
        <figure className="my-6">
          {/* Wide tables scroll inside their own container so the page body
              never scrolls horizontally on a phone. */}
          <div className="overflow-x-auto rounded-card border border-border">
            <table className="w-full border-collapse text-left text-sm">
              {block.caption ? <caption className="sr-only">{block.caption}</caption> : null}
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  {block.headers.map((header) => (
                    <th
                      key={header}
                      scope="col"
                      className="px-4 py-3 font-semibold text-foreground"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, rowIndex) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: authored row order is the identity
                  <tr key={rowIndex} className="border-b border-border/60 last:border-0">
                    {row.map((cell, cellIndex) => (
                      <td
                        // biome-ignore lint/suspicious/noArrayIndexKey: cells are positional
                        key={cellIndex}
                        className={cn(
                          "px-4 py-3 align-top leading-relaxed",
                          cellIndex === 0 ? "text-foreground" : "text-muted-foreground",
                        )}
                      >
                        {renderInline(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {block.caption ? (
            <figcaption className="mt-2 text-sm text-subtle-foreground">{block.caption}</figcaption>
          ) : null}
        </figure>
      );

    case "code":
      return (
        <figure className="my-6">
          <CodeBlock code={block.code} language={block.language} />
          {block.caption ? (
            <figcaption className="mt-2 text-sm text-subtle-foreground">{block.caption}</figcaption>
          ) : null}
        </figure>
      );

    case "callout": {
      const { Icon, className, iconClassName, fallbackTitle } = CALLOUT[block.variant];
      const title = block.title ?? fallbackTitle;
      // The limits callout is a real section — it gets an h2 so it earns a
      // contents entry and an anchor instead of being attributed to whatever
      // heading happened to precede it.
      const Heading = block.variant === "limits" ? "h2" : "p";
      return (
        <aside className={cn("my-8 rounded-card border px-5 py-4", className)}>
          <Heading
            id={id}
            className={cn(
              "flex scroll-mt-24 items-center gap-2 font-medium text-foreground",
              block.variant === "limits" && "font-sans text-lg font-semibold",
            )}
          >
            <Icon className={cn("h-4 w-4 shrink-0", iconClassName)} aria-hidden />
            {title}
          </Heading>
          <p className="mt-2 leading-relaxed text-muted-foreground">{renderInline(block.text)}</p>
        </aside>
      );
    }

    case "faq":
      return (
        <div className="my-8">
          <h2
            id={id}
            className="mt-12 mb-3 scroll-mt-24 font-sans text-2xl font-semibold tracking-tight text-foreground"
          >
            {block.title ?? FAQ_HEADING}
          </h2>
          <div className="divide-y divide-border border-y border-border">
            {block.items.map((item) => (
              <div key={item.q} className="py-5">
                {/* Real headings, not a disclosure widget: the pair stays
                    extractable and no answer is hidden behind interaction. */}
                <h3
                  id={slugify(item.q)}
                  className="scroll-mt-24 text-pretty font-sans text-base font-semibold text-foreground"
                >
                  {item.q}
                </h3>
                <p className="mt-2 leading-relaxed text-muted-foreground">{renderInline(item.a)}</p>
              </div>
            ))}
          </div>
        </div>
      );

    case "dl":
      return (
        <dl className="my-6 space-y-5">
          {block.items.map((item) => (
            <div key={item.term}>
              <dt
                id={`term-${slugify(item.term)}`}
                className="scroll-mt-24 font-semibold text-foreground"
              >
                {item.term}
              </dt>
              <dd className="mt-1 leading-relaxed text-muted-foreground">
                {renderInline(item.def)}
              </dd>
            </div>
          ))}
        </dl>
      );
  }
}

function CodeBlock({
  code,
  language,
  className,
}: {
  code: string;
  language?: string;
  className?: string;
}) {
  return (
    <pre
      className={cn(
        "overflow-x-auto rounded-card border border-border bg-inset p-4 font-mono text-sm leading-relaxed text-foreground",
        className,
      )}
      data-language={language}
    >
      <code>{code}</code>
    </pre>
  );
}
